/**
 * Spreads por Emissor (página nova, sem equivalente no Streamlit).
 *
 * Spread de até 8 emissores contra a referência de mercado gravada no banco, por aba
 * (CDI+, IPCA+) e grupo. O cálculo e os gráficos vêm prontos do backend
 * (`/credito-privado/spreads-por-emissor`); a lista do seletor vem de
 * `/credito-privado/spreads-por-emissor/emissores` (só emissores precificados hoje).
 * Na URL: emissores (CNPJs, vírgula), periodo, estatistica e aba.
 */
import { Alert, Group, Loader, MultiSelect, Stack, Tabs, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { Elementos, type ElementoApi } from '../../components/Elementos';
import { BarraFiltros, CampoOpcoes, GrupoFiltros } from '../../components/Filtros';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { opcoesPeriodo } from '../../lib/periodos';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/credito-privado/spreads-por-emissor';

// Usados enquanto a primeira resposta não chega; a API devolve as listas definitivas.
const PERIODOS = opcoesPeriodo(['ytd', '30d', '6m', '1a', '2a', 'max']);
const ESTATISTICAS = [
  { valor: 'mediana', rotulo: 'Mediana' },
  { valor: 'media', rotulo: 'Média aparada' },
  { valor: 'ponderada', rotulo: 'Média ponderada' },
];
const GRUPOS: { chave: keyof Papeis; rotulo: string }[] = [
  { chave: 'cdi', rotulo: 'CDI+' },
  { chave: 'ipca_isentos', rotulo: 'IPCA+ incentivadas/CRI/CRA' },
  { chave: 'ipca_nao_incent', rotulo: 'IPCA+ não incentivadas' },
];

interface Papeis {
  cdi: number;
  ipca_isentos: number;
  ipca_nao_incent: number;
}

interface Emissor {
  cnpj: string;
  nome: string;
  rotulo: string;
  papeis: Papeis;
}

interface ListaEmissores {
  emissores: Emissor[];
  max_emissores: number;
  dias_precificado: number;
}

interface Opcao {
  valor: string;
  rotulo: string;
}

interface DadosPagina {
  emissores: { cnpj: string; nome: string }[];
  periodo: string;
  periodos: Opcao[];
  estatistica: string;
  estatisticas: Opcao[];
  abas: { titulo: string; elementos: ElementoApi[] }[];
}

/** "CDI+ 1 · IPCA+ incentivadas/CRI/CRA 8" (só os grupos com papel precificado). */
export function resumoPapeis(papeis: Papeis): string {
  return GRUPOS.filter((g) => papeis[g.chave] > 0)
    .map((g) => `${g.rotulo} ${papeis[g.chave]}`)
    .join(' · ');
}

function mensagemErro(erro: unknown): string {
  return erro instanceof ErroApi ? erro.message : 'Não foi possível carregar os dados.';
}

export function SpreadsPorEmissor() {
  const [f, definir] = useFiltrosUrl({
    emissores: '',
    periodo: 'ytd',
    estatistica: 'mediana',
    aba: '',
  });
  const selecionados = useMemo(() => f.emissores.split(',').filter(Boolean), [f.emissores]);

  const lista = useQuery({
    queryKey: [`${CAMINHO}/emissores`],
    queryFn: ({ signal }) =>
      apiGet<Envelope<ListaEmissores>>(`${CAMINHO}/emissores`, undefined, signal),
    staleTime: 30 * 60 * 1000,
  });
  const emissores = useMemo(() => lista.data?.data.emissores ?? [], [lista.data]);
  const max = lista.data?.data.max_emissores ?? 8;
  const porCnpj = useMemo(() => new Map(emissores.map((e) => [e.cnpj, e])), [emissores]);

  const consulta = useQuery({
    queryKey: [CAMINHO, f.emissores, f.periodo, f.estatistica],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosPagina>>(
        CAMINHO,
        { emissores: f.emissores, periodo: f.periodo, estatistica: f.estatistica },
        signal,
      ),
    enabled: selecionados.length > 0,
    placeholderData: keepPreviousData,
  });
  const dados = selecionados.length ? consulta.data?.data : undefined;
  const abaAtiva = (dados?.abas.some((a) => a.titulo === f.aba) && f.aba) || dados?.abas[0]?.titulo;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Spreads por Emissor"
        descricao="Spread dos papéis de cada emissor contra a referência de mercado das debêntures."
        dadosAte={dados ? consulta.data?.meta.dados_ate : undefined}
        fonte="ANBIMA (banco Persevera) e cadastro do Fibery"
      />
      <BarraFiltros carregando={lista.isFetching || consulta.isFetching}>
        <GrupoFiltros titulo="Emissores">
          <MultiSelect
            label="Emissores"
            placeholder={
              lista.isPending
                ? 'Carregando emissores…'
                : selecionados.length
                  ? ''
                  : 'Digite o nome ou o CNPJ'
            }
            description={`Até ${max}, com papel precificado pela ANBIMA hoje`}
            data={emissores.map((e) => ({ value: e.cnpj, label: e.rotulo }))}
            value={selecionados}
            onChange={(v) => definir({ emissores: v.join(','), aba: '' })}
            renderOption={({ option }) => {
              const e = porCnpj.get(option.value);
              return (
                <Stack gap={0}>
                  <Text size="sm">{option.label}</Text>
                  {e && (
                    <Text size="xs" c="dimmed">
                      {resumoPapeis(e.papeis)}
                    </Text>
                  )}
                </Stack>
              );
            }}
            maxValues={max}
            disabled={lista.isPending}
            rightSection={lista.isPending ? <Loader size="xs" /> : undefined}
            searchable
            clearable
            limit={200}
            nothingFoundMessage="Nenhum emissor encontrado"
            w={520}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Histórico">
          <CampoOpcoes
            rotulo="Período"
            valor={f.periodo}
            aoMudar={(periodo) => definir({ periodo })}
            opcoes={dados?.periodos ?? PERIODOS}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Estatística">
          <CampoOpcoes
            rotulo="Medida"
            valor={f.estatistica}
            aoMudar={(estatistica) => definir({ estatistica })}
            opcoes={dados?.estatisticas ?? ESTATISTICAS}
          />
        </GrupoFiltros>
      </BarraFiltros>

      {lista.isError && <Alert color="red">{mensagemErro(lista.error)}</Alert>}
      {!selecionados.length && (
        <Alert color="blue">
          <Group gap="xs">Escolha até {max} emissores para comparar.</Group>
        </Alert>
      )}
      {selecionados.length > 0 && consulta.isError && (
        <Alert color="red">{mensagemErro(consulta.error)}</Alert>
      )}
      {selecionados.length > 0 && consulta.isPending && <Loader />}
      {dados && (
        <>
          <AvisosApi avisos={[lista.data?.meta.avisos, consulta.data?.meta.avisos]} />
          <Secao
            titulo="Spread por emissor x mercado"
            legenda="Por grupo de papéis: a série de cada emissor e a do mercado (tracejada), a diferença em pontos-base e os papéis na última data. O mercado é só de debêntures; CRI/CRA não têm volume em circulação e ficam fora da média ponderada. Debêntures não incentivadas aparecem em bloco separado."
          >
            <Tabs
              value={abaAtiva}
              onChange={(aba) => aba && definir({ aba })}
              keepMounted={false}
              color="dourado"
            >
              <Tabs.List>
                {dados.abas.map((aba) => (
                  <Tabs.Tab key={aba.titulo} value={aba.titulo}>
                    {aba.titulo}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              {dados.abas.map((aba) => (
                <Tabs.Panel key={aba.titulo} value={aba.titulo} pt="md">
                  <Elementos elementos={aba.elementos} />
                </Tabs.Panel>
              ))}
            </Tabs>
          </Secao>
        </>
      )}
    </Pagina>
  );
}
